import React, { useState, useEffect, useContext, useMemo, useRef } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQuery, gql, useMutation } from "@apollo/client";
import StarekContext from "../../context/StarekContext";
import EntityLoadingState from "../../components/EntityLoadingState";
import EntityEmptyState from "../../components/EntityEmptyState";
import { EMPTY_MESSAGES } from "../../constants/emptyStateMessages";
import ShiftSidebar from "./ShiftSidebar";
import ShiftsCalendarView from "./ShiftsCalendarView";
import ShiftListingView from "./ShiftListingView";
import EntityOperationBar from "../../components/EntityOperationBar";
import ConfirmModal from "../../components/modals/ConfirmModal";
import { usePermissions } from "../../hooks/usePermissions";
import { buildListRoute, isBitSet, parseRouteEntityId } from "../../utils";
import usePagedList, { sortToOrderBy } from "../../hooks/usePagedList";
import Paginator from "../../components/Paginator";
import { deactivateData, restoreData } from "../../utils/softDelete";
import "../../starek.css";

const DAYS_OF_WEEK = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
];

const formatTime = (sec) => {
    if (sec == null) return "-";
    const d = new Date(0);
    d.setSeconds(sec);
    let h = d.getUTCHours();
    const m = d.getUTCMinutes().toString().padStart(2, "0");
    const ampm = h >= 12 ? "pm" : "am";
    h = h % 12 || 12;
    return `${h}:${m} ${ampm}`;
};

// UI Strings
const UI_STRINGS = {
    PAGE_TITLE: "Shifts",
    SEARCH_PLACEHOLDER: "Search",
    VIEW_MODE_TIMELINE: "Timeline View",
    VIEW_MODE_LIST: "List View",
    ENTITY_SINGULAR: "Shift",
    ENTITY_PLURAL: "Shifts",
    DELETE_TITLE_SINGLE: (name) => `Delete Shift ${name}?`,
    DELETE_TITLE_BULK: (count) => `Delete ${count} Shifts?`,
    DELETE_MESSAGE_SINGLE: (name) => `You are about to delete shift "${name}".

This action cannot be undone.

Are you sure you wish to continue?`,
    DELETE_MESSAGE_BULK: (
        count,
    ) => `You are about to delete ${count} shift${count !== 1 ? "s" : ""}.

This action cannot be undone.

Are you sure you wish to continue?`,
    DELETE_CONFIRM: "Delete",
    DELETE_CANCEL: "Cancel",
    TOTAL_SELECTED: (count) => `Total shifts selected: ${count}`,
    NOT_FOUND: "Shift not found",
};

const runsOnToDays = (mask) => {
    const days = [];
    DAYS_OF_WEEK.forEach((day, i) => {
        if (isBitSet(mask, i)) days.push(day.slice(0, 3));
    });
    return days.length ? days.join(", ") : "-";
};

const parseShiftData = (shifts) => {
    const dayShifts = DAYS_OF_WEEK.map((day) => ({ day, ranges: [] }));
    shifts.forEach((shift) => {
        const { start_time, end_time, runs_on } = shift;
        DAYS_OF_WEEK.forEach((_, index) => {
            if (isBitSet(runs_on, index)) {
                dayShifts[index].ranges.push({
                    shift_id: shift.id,
                    shift_name: shift.name,
                    start: start_time,
                    end: end_time,
                });
            }
        });
    });
    return dayShifts;
};

// Everything the timeline, the table and the sidebar read off a shift.
const SHIFT_FIELDS = `
    id
    name
    description
    is_active
    deleted_at
    start_time
    end_time
    runs_on
    created_at
    updated_at
    created_by {
        id
        name
    }
    updated_by {
        id
        name
    }
`;

// Every shift of the plant, for the timeline view (a week grid needs them all).
const GET_SHIFT_LIST_FOR_PLANT = gql`
    query GetShifts($where: ShiftWhereInput!) {
        shifts(where: $where) {
            ${SHIFT_FIELDS}
        }
    }
`;

// One page of shifts plus the total, for the list view (usePagedList).
const GET_SHIFTS_PAGE = gql`
    query GetShiftsPage(
        $where: ShiftWhereInput!
        $orderBy: [ShiftOrderByWithRelationInput!]
        $take: Int
        $skip: Int
    ) {
        shifts(where: $where, orderBy: $orderBy, take: $take, skip: $skip) {
            ${SHIFT_FIELDS}
        }
        aggregateShift(where: $where) {
            _count {
                _all
            }
        }
    }
`;

// A deep link (/shifts/:id) may point at a shift that is not on the current page, or
// is inactive while the list hides inactive shifts. findFirst goes through the
// soft-delete filter, so an archived shift is not found.
const GET_SHIFT_BY_ID = gql`
    query GetShiftById($where: ShiftWhereInput!) {
        findFirstShift(where: $where) {
            ${SHIFT_FIELDS}
        }
    }
`;

const UPDATE_MANY_SHIFT = gql`
    mutation UpdateManyShift($data: ShiftUpdateManyMutationInput!, $where: ShiftWhereInput) {
        updateManyShift(data: $data, where: $where) {
            count
        }
    }
`;

const DELETE_MANY_SHIFTS = gql`
    mutation DeleteManyShift($ids: [Int!]!) {
        deleteManyShift(where: { id: { in: $ids } }) {
            count
        }
    }
`;

const DELETE_ONE_SHIFT = gql`
    mutation DeleteOneShift($id: Int!) {
        deleteOneShift(where: { id: $id }) {
            id
        }
    }
`;

const CREATE_ONE_SHIFT = gql`
    mutation CreateOneShift($data: ShiftCreateInput!) {
        createOneShift(data: $data) {
            id
            name
            description
            start_time
            end_time
            runs_on
            created_at
        }
    }
`;

const UPDATE_ONE_SHIFT = gql`
    mutation UpdateOneShift(
        $data: ShiftUpdateInput!
        $where: ShiftWhereUniqueInput!
    ) {
        updateOneShift(data: $data, where: $where) {
            id
            name
            description
            start_time
            end_time
            runs_on
            updated_at
        }
    }
`;

const Shifts = () => {
    const starekContext = useContext(StarekContext);
    const navigate = useNavigate();
    const { id: routeId } = useParams();
    const routeShiftId = parseRouteEntityId(routeId);
    const [searchParams, setSearchParams] = useSearchParams();

    const { can } = usePermissions();
    const canWrite = can("shift", "WRITE");
    const canDestroy = can("shift", "DESTROY");

    // View state lives in the URL so a refresh, a deep link or a back-navigation
    // lands on exactly the same list the user was looking at.
    const showInactive = searchParams.get("showInactive") === "1";

    const patchSearchParams = (patch, options = {}) => {
        const next = new URLSearchParams(searchParams);
        Object.entries(patch).forEach(([key, value]) => {
            if (value === null || value === undefined || value === "") {
                next.delete(key);
            } else {
                next.set(key, value);
            }
        });
        setSearchParams(next, options);
    };

    const setShowInactive = (value, options = {}) =>
        patchSearchParams({ showInactive: value ? "1" : null }, options);

    const shiftQueryWhere = {
        plant_id: { equals: starekContext.lastUsedPlant.id },
        ...(showInactive ? {} : { is_active: { equals: true } }),
    };

    const [filterText, setFilterText] = useState("");
    // Read the view straight from the URL on the first render, so a ?view=list link
    // does not fire the timeline's full query first.
    const [viewMode, setViewMode] = useState(() =>
        searchParams.get("view") === "list" ? "list" : "timeline",
    );
    const isListView = viewMode === "list";
    const [selectedRows, setSelectedRows] = useState([]);

    // Timeline view: every shift of the plant (a week grid cannot be paged). Only run
    // while the timeline is showing — the list view reads its own page below.
    // network-only on every run, not just the first: flipping "Show Inactive" swaps the
    // query variables, and under the default cache-first Apollo replays whatever that
    // variable set returned last time — a list captured before the shift was deactivated
    // or restored (same fix as Parts, CPRN00117-901).
    const {
        data,
        previousData,
        loading: fullLoading,
        refetch: refetchFull,
    } = useQuery(GET_SHIFT_LIST_FOR_PLANT, {
        client: starekContext.engineClient,
        skip: isListView,
        variables: { where: shiftQueryWhere },
        fetchPolicy: "network-only",
        nextFetchPolicy: "network-only",
    });
    const fullShown = data ?? previousData;
    const allShifts = useMemo(() => fullShown?.shifts ?? [], [fullShown]);

    // List view: table sort, applied on the server ({ field, direction } or null).
    const [sort, setSort] = useState(null);
    const orderBy = useMemo(() => sortToOrderBy(sort, ["description"]), [sort]);

    // List view: one page (12) of shifts from the server, searched and sorted there.
    const {
        items: pageItems,
        total,
        page,
        pageCount,
        pageSize,
        setPage,
        loading: pageLoading,
        initialLoading: pageInitialLoading,
        refetch: refetchPage,
    } = usePagedList({
        query: GET_SHIFTS_PAGE,
        listField: "shifts",
        countField: "aggregateShift",
        where: shiftQueryWhere,
        searchText: filterText,
        orderBy,
        client: starekContext.engineClient,
        skip: !isListView,
    });

    // /shifts/:id may point at a shift that is not on this page (list view) or is
    // inactive while inactive shifts are hidden, so it is loaded on its own.
    const {
        data: routeShiftData,
        loading: routeShiftLoading,
        refetch: refetchRouteShift,
    } = useQuery(GET_SHIFT_BY_ID, {
        client: starekContext.engineClient,
        skip: routeShiftId == null,
        variables: {
            where: {
                id: { equals: routeShiftId },
                plant_id: { equals: starekContext.lastUsedPlant.id },
            },
        },
        fetchPolicy: "network-only",
    });

    // Rows the active view is showing — the route effect prefers these (fresh after
    // every refresh) over the by-id copy.
    const activeRows = isListView ? pageItems : allShifts;

    // updateManyShift and the delete mutations only return a count, so the list has to
    // be re-read after a write. Only the active view's query is running, so only that
    // one is refetched. A failed refetch must not surface as a failed write.
    // After any write, re-read the open record too (unless it was just deleted): the
    // page rows from before the refetch cannot tell whether it is still on the page, and
    // updateMany returns only a count, so the cached by-id copy would otherwise go stale.
    const hardRefresh = async () => {
        try {
            await (isListView ? refetchPage() : refetchFull());
            // The open shift may not be in the active rows; re-read it so the sidebar
            // sees the write too. A just-deleted shift is skipped — it would read as
            // "not found".
            if (
                routeShiftId != null &&
                !deletedIdsRef.current.has(routeShiftId)
            ) {
                await refetchRouteShift();
            }
        } catch (err) {
            console.error("Failed to refresh shifts:", err);
        }
    };

    useEffect(() => {
        const viewParam = searchParams.get("view");
        if (viewParam && (viewParam === "timeline" || viewParam === "list")) {
            setViewMode(viewParam);
        }
    }, [searchParams]);
    const [toggleCleared, setToggleCleared] = useState(false);

    // Selection is per page: moving pages clears it.
    useEffect(() => {
        setSelectedRows([]);
        setToggleCleared((p) => !p);
    }, [page]);
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteDone, setDeleteDone] = useState(0);
    const [showAddShiftDrawer, setShowAddShiftDrawer] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [routeEditShift, setRouteEditShift] = useState(null);
    const [confirmCfg, setConfirmCfg] = useState({ open: false });
    // Ids just deleted from the sidebar: the post-delete refetch drops them from the
    // list while the route still points at them, which must not read as "not found".
    const deletedIdsRef = useRef(new Set());

    const [deleteManyShifts] = useMutation(DELETE_MANY_SHIFTS, {
        client: starekContext.engineClient,
    });
    const [deleteOneShift] = useMutation(DELETE_ONE_SHIFT, {
        client: starekContext.engineClient,
    });
    const [updateManyShift] = useMutation(UPDATE_MANY_SHIFT, { client: starekContext.engineClient });
    const [togglingActiveId, setTogglingActiveId] = useState(null);
    const [createShift] = useMutation(CREATE_ONE_SHIFT, {
        client: starekContext.engineClient,
    });
    const [updateShift] = useMutation(UPDATE_ONE_SHIFT, {
        client: starekContext.engineClient,
    });

    useEffect(() => {
        if (!routeId) {
            setRouteEditShift(null);
            return;
        }

        if (routeShiftLoading) return;

        const listRoute = buildListRoute("/shifts", searchParams);

        if (routeShiftId == null) {
            starekContext.showError(UI_STRINGS.NOT_FOUND);
            setRouteEditShift(null);
            navigate(listRoute, { replace: true });
            return;
        }

        // Just deleted from the sidebar: the sidebar closes itself. Checked before the
        // lookup, because the by-id result can still hold the deleted row for a moment.
        if (deletedIdsRef.current.has(routeShiftId)) return;

        // Prefer the row the active view shows; otherwise the shift loaded by id.
        // Either way an inactive shift opens without widening the list.
        const shiftToEdit =
            activeRows.find((shift) => Number(shift?.id) === routeShiftId) ||
            routeShiftData?.findFirstShift;

        if (!shiftToEdit) {
            starekContext.showError(UI_STRINGS.NOT_FOUND);
            setRouteEditShift(null);
            navigate(listRoute, { replace: true });
            return;
        }

        setRouteEditShift(shiftToEdit);
        setSelectedRows((current) =>
            current.length === 1 && Number(current[0]?.id) === routeShiftId
                ? current
                : [shiftToEdit],
        );
        setIsEditMode(true);
        setShowAddShiftDrawer(true);
    }, [
        routeId,
        routeShiftId,
        routeShiftLoading,
        routeShiftData,
        activeRows,
        searchParams,
        navigate,
        starekContext,
    ]);

    // The full-page loader is for the page's first load only (see the render below).
    const activeInitialLoading = isListView
        ? pageInitialLoading
        : fullLoading && !fullShown;
    const [hasLoaded, setHasLoaded] = useState(false);
    useEffect(() => {
        if (!activeInitialLoading) setHasLoaded(true);
    }, [activeInitialLoading]);

    // Timeline only: it holds every shift, so search filters it in the browser. The
    // list view is searched on the server.
    const filteredItems = useMemo(() => {
        const q = filterText.trim().toLowerCase();
        if (!q) return allShifts;
        return allShifts.filter(
            (item) =>
                item.name?.toLowerCase().includes(q) ||
                item.description?.toLowerCase().includes(q),
        );
    }, [allShifts, filterText]);

    const handleRowClicked = (row) => {
        navigate(buildListRoute(`/shifts/${row.id}`, searchParams));
    };

    const handleAdd = () => {
        setRouteEditShift(null);
        setIsEditMode(false);
        setShowAddShiftDrawer(true);
    };

    const handleEdit = () => {
        if (selectedRows.length !== 1) return;
        navigate(buildListRoute(`/shifts/${selectedRows[0].id}`, searchParams));
    };

    const handleDrawerClose = async () => {
        setShowAddShiftDrawer(false);
        setIsEditMode(false);
        setRouteEditShift(null);
        if (routeId) {
            navigate(buildListRoute("/shifts", searchParams), {
                replace: true,
            });
        }
        await hardRefresh();
        setToggleCleared((p) => !p);
        setSelectedRows([]);
    };

    const openConfirm = (cfg) => setConfirmCfg({ open: true, ...cfg });
    const closeConfirm = () => setConfirmCfg({ open: false });

    const handleSaveShift = async (shiftPayload) => {
        try {
            const currentUserId =
                starekContext?.currentUser?.id ||
                starekContext?.user?.id ||
                starekContext?.me?.id ||
                2;

            let saved = { id: shiftPayload.id };
            if (!shiftPayload.id) {
                // Create
                const result = await createShift({
                    variables: {
                        data: {
                            name: shiftPayload.name,
                            description: shiftPayload.description || "",
                            start_time: shiftPayload.start_time,
                            end_time: shiftPayload.end_time,
                            runs_on: shiftPayload.runs_on,
                            plant: {
                                connect: {
                                    id: starekContext.lastUsedPlant.id,
                                },
                            },
                            created_by: {
                                connect: { id: Number(currentUserId) },
                            },
                            updated_by: {
                                connect: { id: Number(currentUserId) },
                            },
                        },
                    },
                });
                saved = result?.data?.createOneShift ?? saved;
            } else {
                // Update
                await updateShift({
                    variables: {
                        where: { id: shiftPayload.id },
                        data: {
                            name: { set: shiftPayload.name },
                            description: {
                                set: shiftPayload.description || "",
                            },
                            start_time: { set: shiftPayload.start_time },
                            end_time: { set: shiftPayload.end_time },
                            runs_on: { set: shiftPayload.runs_on },
                            updated_by: {
                                connect: { id: Number(currentUserId) },
                            },
                        },
                    },
                });
            }

            // The sidebar stays open to confirm the save and point at the next screen;
            // it closes when the user picks a next step or presses Done.
            await hardRefresh();
            starekContext.showSuccess(
                shiftPayload.id
                    ? `Shift "${shiftPayload.name}" updated successfully`
                    : `Shift "${shiftPayload.name}" created successfully`,
            );
            return saved;
        } catch (error) {
            starekContext.showError(error);
            throw error;
        }
    };

    // Toolbar bulk delete and sidebar delete both confirm here. `onSettled(ok)` lets the
    // sidebar stay open until the dialog is answered (true only after a successful delete).
    const handleDelete = (shifts, onSettled) => {
        if (!shifts || shifts.length === 0 || isDeleting) {
            onSettled?.(false);
            return;
        }

        const isSingle = shifts.length === 1;
        const title = isSingle
            ? UI_STRINGS.DELETE_TITLE_SINGLE(shifts[0].name)
            : UI_STRINGS.DELETE_TITLE_BULK(shifts.length);

        const message = isSingle
            ? UI_STRINGS.DELETE_MESSAGE_SINGLE(shifts[0].name)
            : UI_STRINGS.DELETE_MESSAGE_BULK(shifts.length);

        openConfirm({
            title: title,
            content: (
                <>
                    <div
                        className="text-dark"
                        style={{ whiteSpace: "pre-wrap" }}
                    >
                        {message}
                    </div>
                    <p className="mt-4 text-dark fw-bold">
                        {UI_STRINGS.TOTAL_SELECTED(shifts.length)}
                    </p>
                </>
            ),
            confirmText: UI_STRINGS.DELETE_CONFIRM,
            cancelText: UI_STRINGS.DELETE_CANCEL,
            type: "danger",
            onConfirm: async () => {
                setIsDeleting(true);
                setDeleteDone(0);
                const ids = shifts.map((r) => r.id);
                let succeeded = false;

                try {
                    await deleteManyShifts({ variables: { ids } });
                    succeeded = true;
                    setDeleteDone(ids.length);
                    starekContext.showSuccess(
                        `Successfully deleted ${ids.length} shift(s)`,
                    );
                } catch (_) {
                    succeeded = true;
                    for (const id of ids) {
                        try {
                            await deleteOneShift({ variables: { id } });
                            setDeleteDone((d) => d + 1);
                        } catch (err) {
                            starekContext.showError(err);
                            succeeded = false;
                            break;
                        }
                    }
                } finally {
                    if (succeeded) deletedIdsRef.current = new Set(ids);
                    setIsDeleting(false);
                    setToggleCleared((p) => !p);
                    setSelectedRows([]);
                    await hardRefresh();
                    closeConfirm();
                    onSettled?.(succeeded);
                }
            },
            onClose: () => {
                closeConfirm();
                onSettled?.(false);
            },
        });
    };

    const handleToggleActive = async (shifts, makeActive) => {
        const userId = starekContext?.currentUser?.id ?? starekContext?.user?.id;
        const ids = shifts.map((s) => s.id);
        try {
            await updateManyShift({
                variables: {
                    data: makeActive ? restoreData() : deactivateData(),
                    where: { id: { in: ids } },
                },
            });
            starekContext.showSuccess(makeActive ? `Restored ${ids.length} shift(s)` : `Deactivated ${ids.length} shift(s)`);
            setSelectedRows([]);
            setToggleCleared((p) => !p);
            await hardRefresh();
        } catch (err) {
            starekContext.showError(err);
        }
    };

    const handleToggleActiveSingle = async (shift) => {
        const userId = starekContext?.currentUser?.id ?? starekContext?.user?.id;
        setTogglingActiveId(shift.id);
        try {
            const deactivating = shift.is_active;
            await updateManyShift({
                variables: {
                    data: deactivating ? deactivateData() : restoreData(),
                    where: { id: { in: [shift.id] } },
                },
            });
            starekContext.showSuccess(
                deactivating
                    ? `${shift.name} is deactivated`
                    : `${shift.name} is restored`,
            );
            await hardRefresh();
        } catch (err) {
            starekContext.showError(err);
        } finally {
            setTogglingActiveId(null);
        }
    };

    const shiftExtendedActions = selectedRows.length > 0 ? [
        {
            label: "Deactivate",
            icon: "fa-ban",
            danger: true,
            disabled: selectedRows.every((r) => !r.is_active),
            onClick: (rows) => handleToggleActive(rows.filter((r) => r.is_active), false),
        },
        {
            label: "Restore",
            icon: "fa-undo",
            disabled: selectedRows.every((r) => r.is_active),
            onClick: (rows) => handleToggleActive(rows.filter((r) => !r.is_active), true),
        },
    ] : [];

    // Only the page's first load shows the full-page loader. A Show Inactive toggle, a
    // refetch or a view switch keeps the page mounted (the operation bar holds the
    // search box text), so the sidebar and dialog are not torn down mid-action.
    if (activeInitialLoading && !hasLoaded) return <EntityLoadingState icon="fa-calendar" />;

    const activeShift = routeEditShift || (isEditMode ? selectedRows[0] : null);

    return (
        <div className="container-fluid">
            <div className="font-weight-bold mb-3">{UI_STRINGS.PAGE_TITLE}</div>

            <EntityOperationBar
                entity={{
                    singular: UI_STRINGS.ENTITY_SINGULAR,
                    plural: UI_STRINGS.ENTITY_PLURAL,
                }}
                allowSearch={true}
                allowAdd={canWrite}
                allowDelete={canDestroy}
                allowEdit={canWrite}
                allowExtendedActions={canWrite && selectedRows.length > 0}
                extendedActions={shiftExtendedActions}
                allowShowInactive={true}
                showInactive={showInactive}
                onShowInactiveChange={(v) => { setShowInactive(v); setSelectedRows([]); }}
                defaultView={viewMode}
                selectedRows={selectedRows}
                isDeleting={isDeleting}
                deleteDone={deleteDone}
                onSearch={(text) => setFilterText(text)}
                onAdd={handleAdd}
                onViewChange={(mode) => {
                    setViewMode(mode);
                    patchSearchParams({ view: mode });
                    setSelectedRows([]);
                }}
                onDelete={() => handleDelete(selectedRows)}
                onEdit={handleEdit}
            >
                <EntityOperationBar.View
                    mode="timeline"
                    icon="fa fa-calendar"
                    tooltip={UI_STRINGS.VIEW_MODE_TIMELINE}
                >
                    <ShiftsCalendarView
                        items={filteredItems}
                        selectedRows={selectedRows}
                        onSelectedRowsChange={({ selectedRows }) =>
                            setSelectedRows(selectedRows)
                        }
                        filterText={filterText}
                        onEventClick={(shift) => {
                            navigate(buildListRoute(`/shifts/${shift.id}`, searchParams));
                        }}
                        onToggleActive={handleToggleActiveSingle}
                        togglingActiveId={togglingActiveId}
                    />
                </EntityOperationBar.View>

                <EntityOperationBar.View
                    mode="list"
                    icon="fa fa-list"
                    tooltip={UI_STRINGS.VIEW_MODE_LIST}
                >
                    <ShiftListingView
                        items={pageItems}
                        selectedRows={selectedRows}
                        onSelectedRowsChange={({ selectedRows }) =>
                            setSelectedRows(selectedRows)
                        }
                        toggleCleared={toggleCleared}
                        loading={pageInitialLoading}
                        noDataComponent={<EntityEmptyState icon="fa-calendar" entityName="Shift" messages={EMPTY_MESSAGES.shifts} isFiltered={!!filterText.trim()} onAdd={handleAdd} />}
                        onRowClicked={handleRowClicked}
                        onSort={setSort}
                    />
                </EntityOperationBar.View>
            </EntityOperationBar>

            {isListView && (
                <Paginator
                    page={page}
                    pageCount={pageCount}
                    pageSize={pageSize}
                    total={total}
                    onChange={setPage}
                    disabled={pageLoading}
                />
            )}

            <ShiftSidebar
                show={showAddShiftDrawer}
                onDiscard={handleDrawerClose}
                item={activeShift}
                plantId={starekContext.lastUsedPlant.id}
                onSave={handleSaveShift}
                onOpenSaved={(saved) =>
                    navigate(buildListRoute(`/shifts/${saved.id}`, searchParams))
                }
                onDelete={(shift) =>
                    new Promise((resolve) => handleDelete([shift], resolve))
                }
                onToggleActive={handleToggleActiveSingle}
            />

            <ConfirmModal
                open={confirmCfg.open}
                title={confirmCfg.title}
                confirmText={confirmCfg.confirmText}
                cancelText={confirmCfg.cancelText}
                type={confirmCfg.type}
                loading={isDeleting}
                onConfirm={confirmCfg.onConfirm}
                onClose={confirmCfg.onClose}
            >
                {confirmCfg.content}
            </ConfirmModal>
        </div>
    );
};

export { Shifts };
